/**
 * All user-visible strings on the assessment flow (EN / HI).
 * Question + option text for Hindi come from `assessmentPack` + `assessmentHi.json` (see `frontend/scripts/emitAssessmentHiJson.mjs`).
 */

const EN = {
  home: 'Home',
  careerDna: 'Career DNA',
  hiJsonHint:
    'A few screens may still show in English while we polish the full Hindi experience—your answers and report are not affected.',
  bootLoading: 'Loading your session…',
  sessionLinkInvalidTitle: 'We couldn’t open this link',
  sessionLinkInvalidBody:
    'The link may be mistyped, expired, or from a different account than the one saved in this browser. Start again from the home page, or open the exact link from your email or message without changing it.',
  sessionLinkInvalidCta: 'Start again',
  sessionLinkInvalidHome: 'Back to home',
  leaveAssessmentTitle: 'Leave the assessment?',
  leaveAssessmentBodyDirectPaid:
    'If you leave now, this test will stay incomplete. Payments already made are not refunded when you exit on purpose. You can still return later using your link if your session is saved.',
  leaveAssessmentBodySchool:
    'If you leave now, this test will stay incomplete. Payment (if any) is handled separately by your parent or school — use the link they were given to continue. You can come back with your school link when you are ready.',
  leaveAssessmentStay: 'Stay and continue',
  leaveAssessmentLeave: 'Leave anyway',
  leaveAssessmentFooterQ1: 'Need to stop?',
  planTitle: 'From confusion to clarity — choose your plan',
  planSubtitle:
    'Affordable, practical career guidance. Pick a tier, then pay securely to unlock your full report and PDF.',
  planFooter: 'No confusing jargon. No unrealistic promises. Just clear career direction.',
  cancel: 'Cancel',
  introYourDetails: 'Your details',
  introName: 'Name',
  introNamePh: 'e.g. Ananya',
  introEmail: 'Email for your PDF',
  introEmailPh: 'your.email@domain.com',
  introEmailHelp: 'For secure download and fulfilment only — no marketing unless you opt in later.',
  introPhone: 'Phone',
  introOptional: 'optional',
  introClass: 'Class / grade',
  introClassPh: 'e.g. Class 10',
  introConsent:
    'I agree to you storing these details to run this assessment and deliver my report, as described in your privacy policy.',
  introClassForRun: 'Class for this assessment',
  introClassForRunHelp: 'Same flow for every class — each grade can use its own wording in the data packs (9–12).',
  class9: 'Class 9',
  class10: 'Class 10',
  class11: 'Class 11',
  class12: 'Class 12',
  sessionRestored:
    'Session restored — this email is tied to your saved progress; it cannot be changed until you start again.',
  checkingDb: 'Connecting securely…',
  dbUnreachableTitle: 'We’re having trouble connecting',
  dbRetry: 'Try again',
  saving: 'Saving…',
  verifyingDb: 'Verifying your connection…',
  errName: 'Please enter your name.',
  errConsent: 'Please confirm consent to continue.',
  errDbWait: 'Still checking your connection — please wait a moment.',
  errDbBlock:
    'We can’t reach our systems right now. Please try again in a few minutes. If this keeps happening, contact us and we will help.',
  leadTimeoutHelp:
    'That step took longer than usual ({seconds}s). Check your internet connection, refresh the page, and try again.',
  leadFetchNetworkHint:
    'We could not complete that request. Check your connection and try again.',
  voucherCodePlaceholder: 'School or partner code',
  unlockBadge: 'Unlock your report',
  unlockTitle: 'One last step',
  unlockBodyVoucher: 'Enter your voucher code to unlock your personalised read and PDF.',
  unlockBodyPayment:
    'Pay securely with Razorpay, or apply a 100% sponsor / partner code if you have one — no card needed.',
  reportEmail: 'Report email',
  changeEmailHint: 'To change it, go back to the start screen (Cancel) and edit your details there.',
  voucherCode: 'Voucher code',
  voucher100note: '(100% off = no card)',
  sponsorHint: 'Have a free-access code from your school or a partner? Enter it above for an instant unlock.',
  reportLine: 'Report:',
  planLine: 'Plan:',
  payRazorpay: 'Pay with Razorpay',
  openingCheckout: 'Opening checkout…',
  razorpayInactive:
    'Online card checkout is temporarily unavailable. If you have a school or partner code, enter it above to continue—or try again a little later.',
  generateReport: 'Generate my report',
  verify: 'Verify',
  verifyBusy: 'Checking…',
  enterCode: 'Enter your code.',
  couldNotSavePlan: 'Could not save your plan.',
  invalidCode: 'Invalid code.',
  appliedVoucher: 'Applied: {pct}% off{suffix}',
  razorpayDescription: 'Career Intelligence Report',
  retryFailed: 'Retry failed.',
  payOrCode: 'Pay with Razorpay, or enter a valid free-access / sponsor code for an instant unlock.',
  sessionLost: 'Your session was lost. Please refresh the page or start again from the beginning.',
  paymentReceived: 'Payment received. Continue to generate your report.',
  checkpoint: 'Checkpoint',
  questionN: 'Question {n} / 34',
  questionStarTitle: 'Question {n}',
  takeYourTime: 'Take your time — pick what feels most like you. There are no wrong answers.',
  levelTransitionLabel: 'Level {level} · transition',
  levelDiscoveryLabel: 'Level {level} · discovery map',
  continue: 'Continue',
  tapToChoose: 'Tap to choose',
  loadingTitle: 'Building Your Report',
  loadingSub:
    "We're shaping your personalised report and your Career DNA Profile ... almost there.",
  resultTitle: "You're all set",
  resultBody:
    'Tap Download for your Career DNA Profile .. your personalized report is inside the PDF.',
  /** Direct result when delivery is browser Print / Save as PDF (no server download button). */
  resultBodyPrintFirst:
    'Use Print / Save as PDF below for your Career Intelligence Profile — the full write-up loads from our server, the same way as on your computer at home.',
  resultGlitch: ' If something glitched, use the note below and try once more.',
  resultNote: 'Keep this tab open while your file prepares. Everything you need is in your personalized report.',
  resultNotePrintFirst:
    'In the print dialog, choose “Save as PDF” or “Microsoft Print to PDF”. No automatic file download — you control when to save.',
  reportRetryPrepareShort: 'Still waiting? Try generating your report again',
  errStepTitle: "We couldn't finish that step",
  errStepHint: 'Wait a moment, then tap download again. If it keeps happening, contact us and we will get the report delivered to you.',
  pdfRetryTitle: 'Your PDF needs one more try',
  pdfRetryHint:
    'Wait about a minute, then tap the button below again. If it still fails, contact us and we will make sure you get your report.',
  downloadPdf: 'Download your PDF',
  printSavePdf: 'Print / Save as PDF',
  /** Shown while the app prepares the print dialog (full HTML fetch + iframe). */
  printPreparingTitle: 'Opening print…',
  printPreparingBody:
    'We are loading your full report for printing. Your browser’s print dialog will open next — choose “Save as PDF” if you want a file.',
  autoDownloadHint:
    'Your download should begin shortly. Keep this tab open until the file appears in your downloads folder.',
  printSavePdfHint:
    'Opens your browser’s print dialog — choose “Save as PDF” (or “Microsoft Print to PDF”). When the full report has loaded from the server, this saves the same written sections as your PDF (decorative images may be placeholders). It does not replace the official server PDF file when download is available.',
  reportPreviewPaidUsePrint:
    'Payment complete — save your full report with Print / Save as PDF below. There is no automatic download on this page.',
  reportPreviewRequiresPayment:
    'Payment is still required before the full printable report can load. Complete checkout from your payment link first.',
  reportPreviewPrintUnavailable:
    'We could not load the full printable report from the server. Check your connection and try Print / Save as PDF again in a moment.',
  preparePdf: 'Prepare & download PDF',
  preparingPdf: 'Preparing your PDF…',
  preparingHint: 'Preparing your report and PDF — first run can take up to a minute. Keep this tab open.',
  notReadyHint: "Not ready yet? Tap again — we'll fetch your report and PDF securely from the server.",
  snapshotTitle: 'Your snapshot — same mix as in your PDF',
  snapshotBody:
    'Percentages are a friendly view of how you showed up across the assessment — the PDF goes deeper with the same profile.',
  traitAnalytical: 'Analytical',
  traitCreative: 'Creative',
  traitStructured: 'Structured',
  traitSocial: 'Social',
  traitPractical: 'Practical',
  traitRisk: 'Risk / curiosity',
  startAgain: 'Start again',
  backHome: 'Back to home',
  previous: 'Previous',
  seoTitle: 'Career DNA Assessment · ChooseRightCareer',
  seoDesc: '34-question discovery flow aligned with our Career DNA model. Get your personalised read.',
  emailRequired: 'Email is required.',
  emailTooLong: 'Email is too long.',
  emailInvalid: 'Enter a valid email address.',
  friendlyPdfChrome: 'We could not finish your PDF on this attempt. Wait a minute, then tap download again.',
  friendlyLlm: 'Report generation is temporarily unavailable. Please try again later.',
  friendlyGeneric: 'Something went wrong. Please try again in a moment.',
  friendlyDbCollation:
    'We could not save your report because of a temporary server issue. Please try again in a few minutes. If it keeps happening, contact us and we will help.',
  /** Shown when production API returns the generic guarded report error (often proxy timeout, AI hiccup, or a huge PDF payload on slow mobile data). */
  friendlyReportServerGuard:
    'The server could not complete the full report step in time. On phones this is often slow mobile data, switching apps (the tab pauses), or a strict network. Try strong Wi‑Fi, keep this tab in the foreground, wait a minute, then tap to try again. If you use “Download PDF”, the first run can take several minutes.',
  friendlyNetwork:
    'We could not reach our servers from this browser. Check your Wi‑Fi or mobile data, refresh the page, and try again. If you are on a strict school or office network, try another connection once.',
  friendlyNetworkDeployed:
    'We could not reach our servers to finish this step. Check your internet connection, refresh the page, and try again. If the problem continues, please contact us—we are here to help.',
  dbPingSaveHang:
    'Saving is taking longer than expected. Please wait a moment, or refresh and try again.',
  apiVerifyDbFail: 'We could not verify your connection. Check the internet and try again.',
  reportStepTimeout:
    'That step ran longer than {seconds}s and stopped. Wait a few seconds, then tap download to try again.',
  dbCheckFailed: 'Connection check did not complete. Please try again.',
  couldNotStartPayment: 'Could not start payment ({status}).',
  razorpayUnavailable: 'Razorpay is not available.',
  couldNotLoadCheckout: 'Could not load Razorpay checkout.',
  invalidSessionNoId: 'Something went wrong while saving your session. Please refresh the page or start again from the home page.',
  loadingReadLine: 'Building your personalized report {namePart}…',
  phonePlaceholder: '+91…',
  parentStepTitle: 'Parent mobile number',
  parentStepBody:
    "Enter your parent's mobile number. We'll send them a secure text message with a preview link and next steps when available.",
  parentPhoneLabel: 'Parent mobile',
  parentGenerateTitle: 'Building your report',
  parentGenerateCountdownHint:
    'Preparing your secure PDF and parent preview link. The timer shows how long this step has taken so far (not a fixed countdown).',
  parentGenerateAlmost: 'Still working — first run can take up to a minute. Keep this tab open.',
  parentSubmitBusy: 'Working…',
  /** Primary CTA on school parent step — same role as “generate report”, styled like result Print. */
  parentSubmitPreparePrint: 'Prepare report for print',
  schoolResultBody:
    'Your answers are saved and your snapshot is below. Your parent gets a text with a secure preview link and next steps — you do not need to download a PDF from this screen.',
  /** School result when delivery is print-first (no server download button). */
  schoolResultBodyPrintFirst:
    'Your answers are saved and your snapshot is below. Use Print / Save as PDF for your full Career Intelligence write-up — it loads from our server. Your parent also received a text with a secure preview link.',
  schoolResultNote:
    'Keep this tab open on the school network if your teacher asked you to. Use Start again only if you meant to retake the assessment.',
  schoolResultNotePrintFirst:
    'In the print dialog, choose “Save as PDF”. Keep this tab open on the school network if your teacher asked you to.',
  schoolParentSmsNote:
    'We sent your parent a text with a secure link to complete payment and open the full report. They can download the PDF from there after paying.',
  /** Shown on school result when parent-pay and student is not yet paid — no Print button. */
  schoolPrintAfterParentPaysHint:
    'After your parent completes payment on their link, Print / Save as PDF will appear here so you can save the full report. Refresh this page if it does not show up.',
  /** School flow: server print HTML could not be opened (do not use a one-page snapshot). */
  schoolPrintFullReportUnavailable:
    'We could not load the full printable report from the server. Check your connection, wait a moment, and try Print / Save as PDF again.',
  /** Rare: print requested before payment cleared (e.g. stale tab). */
  schoolPrintRequiresPayment:
    'The full report for printing unlocks after payment is complete. Ask your parent to finish payment from their link, then refresh this page.',
  parentNotifyTwilioOk:
    'We sent a text to your parent (reference: {sid}). If they do not see it within a few minutes, ask them to check signal, blocked numbers, and their spam or promotions folder.',
  parentNotifyMock:
    'We could not send a text message from our system just yet. Your report is still being prepared—please try again in a moment, or contact support if this continues.',
};

const HI = {
  home: 'होम',
  careerDna: 'करियर DNA',
  hiJsonHint:
    'कुछ स्क्रीन अभी अंग्रेज़ी में दिख सकती हैं जब तक हम पूरी हिंदी अनुभव पर काम कर रहे हैं—आपके जवाब और रिपोर्ट पर असर नहीं पड़ता।',
  bootLoading: 'आपका सत्र लोड हो रहा है…',
  sessionLinkInvalidTitle: 'हम इस लिंक को नहीं खोल सके',
  sessionLinkInvalidBody:
    'लिंक गलत टाइप हो सकता है, समय सीमा समाप्त हो चुकी है, या इस ब्राउज़र में सहेजे खाते से अलग है। होम से फिर से शुरू करें, या ईमेल/संदेश से मिला वही लिंक बिना बदलाव खोलें।',
  sessionLinkInvalidCta: 'फिर से शुरू करें',
  sessionLinkInvalidHome: 'होम पर वापस',
  leaveAssessmentTitle: 'मूल्यांकन छोड़ें?',
  leaveAssessmentBodyDirectPaid:
    'अभी छोड़ने पर यह टेस्ट अधूरा रहेगा। जानबूझकर बाहर निकलने पर पहले से किया गया भुगतान वापस नहीं होता। सत्र सहेजा हो तो बाद में अपने लिंक से वापस आ सकते हैं।',
  leaveAssessmentBodySchool:
    'अभी छोड़ने पर यह टेस्ट अधूरा रहेगा। भुगतान (यदि हो) अलग से अभिभावक/स्कूल द्वारा होता है — आगे बढ़ने के लिए उन्हें दिया गया लिंक उपयोग करें। तैयार होने पर स्कूल लिंक से वापस आ सकते हैं।',
  leaveAssessmentStay: 'जारी रखें',
  leaveAssessmentLeave: 'फिर भी छोड़ें',
  leaveAssessmentFooterQ1: 'रोकना है?',
  planTitle: 'उलझन से स्पष्टता — अपना प्लान चुनें',
  planSubtitle:
    'किफायती, व्यावहारिक करियर मार्गदर्शन। एक टियर चुनें, फिर पूरा रिपोर्ट और PDF अनलॉक करने के लिए सुरक्षित भुगतान करें।',
  planFooter: 'कोई कठिन शब्द नहीं, कोई अवास्तविक वादा नहीं — साफ़ करियर दिशा।',
  cancel: 'रद्द करें',
  introYourDetails: 'आपकी जानकारी',
  introName: 'नाम',
  introNamePh: 'जैसे अनन्या',
  introEmail: 'PDF के लिए ईमेल',
  introEmailPh: 'aapka.email@domain.com',
  introEmailHelp: 'सुरक्षित डाउनलोड और डिलीवरी के लिए — बिना आपकी अनुमति के मार्केटिंग नहीं।',
  introPhone: 'फ़ोन',
  introOptional: 'वैकल्पिक',
  introClass: 'कक्षा / ग्रेड',
  introClassPh: 'जैसे कक्षा 10',
  introConsent:
    'मैं सहमत हूँ कि आप यह विवरण इस मूल्यांकन और मेरी रिपोर्ट डिलीवरी के लिए संग्रहीत करें, जैसा आपकी गोपनीयता नीति में बताया गया है।',
  introClassForRun: 'इस मूल्यांकन के लिए कक्षा',
  introClassForRunHelp:
    'हर कक्षा के लिए वही फ्लो — प्रत्येक ग्रेड के लिए डेटा पैक (9–12) में अपनी भाषा रख सकते हैं।',
  class9: 'कक्षा 9',
  class10: 'कक्षा 10',
  class11: 'कक्षा 11',
  class12: 'कक्षा 12',
  sessionRestored:
    'सत्र बहाल — यह ईमेल आपकी सहेजी प्रगति से जुड़ा है; दोबारा शुरू करने तक इसे नहीं बदला जा सकता।',
  checkingDb: 'सुरक्षित कनेक्ट हो रहे हैं…',
  dbUnreachableTitle: 'कनेक्ट करने में दिक्कत',
  dbRetry: 'फिर कोशिश करें',
  saving: 'सहेजा जा रहा है…',
  verifyingDb: 'कनेक्शन सत्यापित हो रहा है…',
  errName: 'कृपया अपना नाम लिखें।',
  errConsent: 'जारी रखने के लिए सहमति चुनें।',
  errDbWait: 'कनेक्शन अभी भी जाँच हो रहा है — कृपया थोड़ा इंतज़ार करें।',
  errDbBlock:
    'अभी हमारे सिस्टम तक पहुँच नहीं हो पा रही। कुछ मिनट बाद फिर कोशिश करें। बार-बार हो तो हमसे संपर्क करें—हम मदद करेंगे।',
  leadTimeoutHelp:
    'यह चरण सामान्य से ज़्यादा समय ले रहा है ({seconds}s)। इंटरनेट जाँचें, पृष्ठ रीफ़्रेश करें और फिर कोशिश करें।',
  leadFetchNetworkHint:
    'यह अनुरोध पूरा नहीं हो सका। कनेक्शन जाँचें और फिर कोशिश करें।',
  voucherCodePlaceholder: 'स्कूल या साझेदार कोड',
  unlockBadge: 'अपनी रिपोर्ट अनलॉक करें',
  unlockTitle: 'आखिरी कदम',
  unlockBodyVoucher: 'अपना वाउचर कोड डालें ताकि आपकी व्यक्तिगत पढ़ और PDF अनलॉक हो।',
  unlockBodyPayment:
    'Razorpay से सुरक्षित भुगतान करें, या 100% प्रायोजक/साझेदार कोड लगाएँ — कार्ड ज़रूरी नहीं।',
  reportEmail: 'रिपोर्ट ईमेल',
  changeEmailHint: 'बदलने के लिए शुरुआती स्क्रीन पर जाएँ (रद्द) और वहाँ ईमेल संपादित करें।',
  voucherCode: 'वाउचर कोड',
  voucher100note: '(100% छूट = बिना कार्ड)',
  sponsorHint: 'स्कूल या साझेदार से मुफ़्त कोड है? ऊपर डालें तुरंत अनलॉक।',
  reportLine: 'रिपोर्ट:',
  planLine: 'प्लान:',
  payRazorpay: 'Razorpay से भुगतान',
  openingCheckout: 'चेकआउट खुल रहा है…',
  razorpayInactive:
    'ऑनलाइन कार्ड चेकआउट अस्थायी रूप से उपलब्ध नहीं है। स्कूल या साझेदार कोड हो तो ऊपर डालें—या थोड़ी देर बाद फिर कोशिश करें।',
  generateReport: 'मेरी रिपोर्ट बनाएँ',
  verify: 'सत्यापित करें',
  verifyBusy: 'जाँच हो रही है…',
  enterCode: 'कोड डालें।',
  couldNotSavePlan: 'प्लान सहेजा नहीं जा सका।',
  invalidCode: 'अमान्य कोड।',
  appliedVoucher: 'लागू: {pct}% छूट{suffix}',
  razorpayDescription: 'करियर इंटेलिजेंस रिपोर्ट',
  retryFailed: 'दोबारा प्रयास असफल।',
  payOrCode: 'Razorpay से भुगतान करें, या मान्य मुफ़्त/प्रायोजक कोड डालें।',
  sessionLost: 'सत्र खो गया। पृष्ठ रीफ़्रेश करें या शुरू से फिर आएँ।',
  paymentReceived: 'भुगतान मिल गया। रिपोर्ट बनाने के लिए आगे बढ़ें।',
  checkpoint: 'चेकपॉइंट',
  questionN: 'प्रश्न {n} / 34',
  questionStarTitle: 'प्रश्न {n}',
  takeYourTime: 'समय लें — जो आप जैसा लगे वो चुनें। कोई गलत जवाब नहीं।',
  levelTransitionLabel: 'स्तर {level} · बीच का संदेश',
  levelDiscoveryLabel: 'स्तर {level} · खोज मानचित्र',
  continue: 'आगे बढ़ें',
  tapToChoose: 'चुनने के लिए टैप करें',
  loadingTitle: 'आपकी रिपोर्ट तैयार हो रही है',
  loadingSub: 'आपकी व्यक्तिगत पढ़ और करियर इंटेलिजेंस प्रोफ़ाइल PDF — लगभग तैयार।',
  resultTitle: 'सब तैयार',
  resultBody:
    'डाउनलोड पर टैप करें अपनी करियर इंटेलिजेंस प्रोफ़ाइल के लिए — पूरी लेखनी PDF में है।',
  resultBodyPrintFirst:
    'नीचे प्रिंट / PDF के रूप में सहेजें से अपनी करियर इंटेलिजेंस प्रोफ़ाइल लें — पूरी लेखनी सर्वर से वैसे ही लोड होती है जैसे घर के कंप्यूटर पर।',
  resultGlitch: ' अगर कोई गड़बड़ दिखे, नीचे संदेश देखें और एक बार फिर कोशिश करें।',
  resultNote: 'फ़ाइल तैयार होने तक यह टैब खुला रखें। सब कुछ एक रिपोर्ट में है।',
  resultNotePrintFirst:
    'प्रिंट डायलॉग में “Save as PDF” या “Microsoft Print to PDF” चुनें। कोई स्वचालित डाउनलोड नहीं — सहेजना आप नियंत्रित करते हैं।',
  reportRetryPrepareShort: 'अभी भी इंतज़ार? रिपोर्ट फिर से बनाने की कोशिश करें',
  errStepTitle: 'यह चरण पूरा नहीं हो सका',
  errStepHint: 'थोड़ा इंतज़ार करें, फिर डाउनलोड दोबारा टैप करें। बार-बार हो तो समय बताकर संपर्क करें।',
  pdfRetryTitle: 'PDF के लिए एक बार और',
  pdfRetryHint:
    'लगभग एक मिनट इंतज़ार, फिर नीचे बटन फिर टैप करें। फिर भी न हो तो हमसे संपर्क करें—रिपोर्ट पहुँचाने में मदद करेंगे।',
  downloadPdf: 'PDF डाउनलोड करें',
  printSavePdf: 'प्रिंट / PDF के रूप में सहेजें',
  printPreparingTitle: 'प्रिंट खुल रहा है…',
  printPreparingBody:
    'पूरी रिपोर्ट प्रिंट के लिए लोड हो रही है। अगला चरण ब्राउज़र का प्रिंट डायलॉग है — फ़ाइल के लिए “Save as PDF” चुनें।',
  autoDownloadHint:
    'डाउनलोड थोड़ी देर में शुरू होना चाहिए। फ़ाइल डाउनलोड फ़ोल्डर में दिखने तक यह टैब खुला रखें।',
  printSavePdfHint:
    'ब्राउज़र का प्रिंट डायलॉग खुलेगा — “Save as PDF” या “Microsoft Print to PDF” चुनें। जब सर्वर से पूरी रिपोर्ट लोड हो चुकी हो तो यह PDF जैसे ही लिखित भाग सहेजता है (चित्र अस्थायी हो सकते हैं)। आधिकारिक सर्वर PDF डाउनलोड उपलब्ध हो तो उसकी जगह नहीं लेता।',
  reportPreviewPaidUsePrint:
    'भुगतान पूरा — नीचे प्रिंट / PDF के रूप में सहेजें से पूरी रिपोर्ट सहेजें। इस पृष्ठ पर कोई स्वचालित डाउनलोड नहीं है।',
  reportPreviewRequiresPayment:
    'पूरी प्रिंट योग्य रिपोर्ट से पहले भुगतान ज़रूरी है। अपना भुगतान लिंक खोलकर चेकआउट पूरा करें।',
  reportPreviewPrintUnavailable:
    'सर्वर से पूरी प्रिंट योग्य रिपोर्ट लोड नहीं हो सकी। कनेक्शन जाँचें और कुछ देर बाद प्रिंट / PDF के रूप में सहेजें फिर आज़माएँ।',
  preparePdf: 'तैयार करें और PDF डाउनलोड करें',
  preparingPdf: 'PDF तैयार हो रहा है…',
  preparingHint: 'रिपोर्ट और PDF — पहली बार एक मिनट तक लग सकता है। टैब खुला रखें।',
  notReadyHint: 'अभी तैयार नहीं? फिर टैप करें — सर्वर से सुरक्षित रूप से लाएँगे।',
  snapshotTitle: 'संक्षिप्त झलक — PDF जैसा ही मिश्रण',
  snapshotBody:
    'प्रतिशत दिखाता है कि मूल्यांकन में आप कैसे दिखे — PDF में और विस्तार है।',
  traitAnalytical: 'विश्लेषणात्मक',
  traitCreative: 'रचनात्मक',
  traitStructured: 'संरचित',
  traitSocial: 'सामाजिक',
  traitPractical: 'व्यावहारिक',
  traitRisk: 'जोखिम / जिज्ञासा',
  startAgain: 'फिर से शुरू',
  backHome: 'होम पर वापस',
  previous: 'पिछला',
  seoTitle: 'करियर DNA मूल्यांकन · ChooseRightCareer',
  seoDesc: '34 प्रश्नों की खोज — करियर DNA मॉडल के अनुरूप। अपनी व्यक्तिगत पढ़ पाएँ।',
  emailRequired: 'ईमेल आवश्यक है।',
  emailTooLong: 'ईमेल बहुत लंबा है।',
  emailInvalid: 'मान्य ईमेल पता डालें।',
  friendlyPdfChrome: 'इस बार PDF पूरी नहीं हुई। एक मिनट बाद डाउनलोड फिर टैप करें।',
  friendlyLlm: 'रिपोर्ट जनरेशन अस्थायी रूप से उपलब्ध नहीं। बाद में कोशिश करें।',
  friendlyGeneric: 'कुछ गड़बड़ हुई। क्षण बाद फिर कोशिश करें।',
  friendlyReportServerGuard:
    'पूरी रिपोर्ट चरण समय पर पूरा नहीं हो सका। फोन पर अक्सर धीमा डेटा, दूसरे ऐप पर जाना (टैब रुक जाता है), या सख्त नेटवर्क होता है। मज़बूत Wi‑Fi रखें, टैब अग्रभूमि में रखें, एक मिनट बाद फिर कोशिश करें। पहली बार PDF कई मिनट ले सकता है।',
  friendlyDbCollation:
    'अस्थायी सर्वर समस्या के कारण रिपोर्ट सहेजी नहीं जा सकी। कुछ मिनट बाद फिर कोशिश करें। बार-बार हो तो हमसे संपर्क करें।',
  friendlyNetwork:
    'इस ब्राउज़र से हमारे सर्वर तक नहीं पहुँच पाए। Wi‑Fi या मोबाइल डेटा जाँचें, पृष्ठ रीफ़्रेश करें और फिर कोशिश करें। सख्त स्कूल या ऑफिस नेटवर्क पर दूसरा कनेक्शन आज़माएँ।',
  friendlyNetworkDeployed:
    'यह चरण पूरा करने के लिए हमारे सर्वर तक नहीं पहुँच पाए। इंटरनेट जाँचें, पृष्ठ रीफ़्रेश करें और फिर कोशिश करें। समस्या रहे तो कृपया हमसे संपर्क करें—हम मदद के लिए यहाँ हैं।',
  dbPingSaveHang:
    'सहेजने में सामान्य से ज़्यादा समय लग रहा है। थोड़ा इंतज़ार करें या रीफ़्रेश कर फिर कोशिश करें।',
  apiVerifyDbFail: 'कनेक्शन सत्यापित नहीं हो सका। इंटरनेट जाँचें और फिर कोशिश करें।',
  reportStepTimeout:
    'यह चरण {seconds}s से ज़्यादा चला और रुक गया। कुछ सेकंड बाद डाउनलोड फिर टैप करें।',
  dbCheckFailed: 'कनेक्शन जाँच पूरी नहीं हुई। कृपया फिर कोशिश करें।',
  couldNotStartPayment: 'भुगतान शुरू नहीं हो सका ({status})।',
  razorpayUnavailable: 'Razorpay उपलब्ध नहीं है।',
  couldNotLoadCheckout: 'Razorpay चेकआउट लोड नहीं हो सका।',
  invalidSessionNoId: 'सत्र सहेजते समय कुछ गड़बड़ हुई। पृष्ठ रीफ़्रेश करें या होम से फिर शुरू करें।',
  loadingReadLine: 'आपकी रिपोर्ट तैयार{namePart}…',
  phonePlaceholder: '+91…',
  parentStepTitle: 'अभिभावक का मोबाइल नंबर',
  parentStepBody:
    'अपने अभिभावक का मोबाइल नंबर डालें। जब सेवा उपलब्ध होगी, हम सुरक्षित टेक्स्ट से प्रीव्यू लिंक और अगले कदम भेजेंगे।',
  parentPhoneLabel: 'अभिभावक मोबाइल',
  parentGenerateTitle: 'आपकी रिपोर्ट तैयार हो रही है',
  parentGenerateCountdownHint:
    'सुरक्षित PDF और अभिभावक प्रीव्यू लिंक तैयार हो रहे हैं। टाइमर दिखाता है कि अब तक कितना समय लगा (कोई तय 10 सेकंड नहीं)।',
  parentGenerateAlmost: 'अभी भी चल रहा है — पहली बार में एक मिनट तक लग सकता है।',
  parentSubmitBusy: 'काम जारी…',
  parentSubmitPreparePrint: 'प्रिंट के लिए रिपोर्ट तैयार करें',
  schoolResultBody:
    'आपके जवाब सहेजे गए और नीचे स्नैपशॉट तैयार है। अभिभावक को सुरक्षित प्रीव्यू लिंक SMS से मिला — इस स्क्रीन से PDF डाउनलोड करने की ज़रूरत नहीं।',
  schoolResultBodyPrintFirst:
    'आपके जवाब सहेजे गए और नीचे स्नैपशॉट है। पूरी करियर इंटेलिजेंस रिपोर्ट के लिए प्रिंट / PDF के रूप में सहेजें — यह सर्वर से लोड होती है। अभिभावक को सुरक्षित प्रीव्यू लिंक SMS से भी मिला है।',
  schoolResultNote:
    'अगर शिक्षक ने कहा हो तो स्कूल नेटवर्क पर यह टैब खुला रखें। दोबारा टेस्ट के लिए ही फिर से शुरू करें।',
  schoolResultNotePrintFirst:
    'प्रिंट डायलॉग में “Save as PDF” चुनें। शिक्षक ने कहा हो तो स्कूल नेटवर्क पर टैब खुला रखें।',
  schoolParentSmsNote:
    'अभिभावक को भुगतान और पूरी रिपोर्ट के लिए सुरक्षित लिंक SMS से भेजा गया है। भुगतान के बाद वहीं से PDF डाउनलोड कर सकते हैं।',
  schoolPrintAfterParentPaysHint:
    'जब आपके अभिभावक अपने लिंक से भुगतान पूरा कर लेंगे, यहाँ प्रिंट / PDF के रूप में सहेजें दिखेगा ताकि आप पूरी रिपोर्ट सहेज सकें। न दिखे तो पृष्ठ रीफ़्रेश करें।',
  schoolPrintFullReportUnavailable:
    'सर्वर से पूरी प्रिंट योग्य रिपोर्ट लोड नहीं हो सकी। कनेक्शन जाँचें, थोड़ा इंतज़ार करें, और प्रिंट / PDF के रूप में सहेजें फिर आज़माएँ।',
  schoolPrintRequiresPayment:
    'प्रिंट के लिए पूरी रिपोर्ट तब अनलॉक होती है जब भुगतान पूरा हो जाए। अभिभावक से अपने लिंक से भुगतान पूरा करवाएँ, फिर पृष्ठ रीफ़्रेश करें।',
  parentNotifyTwilioOk:
    'अभिभावक को टेक्स्ट भेज दिया गया (संदर्भ: {sid})। कुछ मिनट में न मिले तो सिग्नल, ब्लॉक नंबर और स्पैम/प्रमोशन फ़ोल्डर जाँचें।',
  parentNotifyMock:
    'अभी सिस्टम से टेक्स्ट नहीं भेजा जा सका। आपकी रिपोर्ट तैयार हो रही है—थोड़ी देर बाद फिर कोशिश करें, या जारी रहे तो सहायता से संपर्क करें।',
};

/** @param {string} [locale] */
export function getAssessmentPageCopy(locale) {
  return String(locale || 'en').toLowerCase().startsWith('hi') ? HI : EN;
}

/** @param {string} template with `{key}` placeholders */
export function fillCopy(template, vars) {
  let s = String(template || '');
  Object.entries(vars || {}).forEach(([k, v]) => {
    s = s.split(`{${k}}`).join(String(v));
  });
  return s;
}

/** Client-side email validation messages (match api/src/emailUtil.js rules). */
export function validateReportEmailClient(raw, required, locale) {
  const c = getAssessmentPageCopy(locale);
  const e = String(raw || '')
    .trim()
    .toLowerCase();
  if (required && !e) return c.emailRequired;
  if (!required && !e) return '';
  if (e.length > 320) return c.emailTooLong;
  if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/.test(e)) return c.emailInvalid;
  return '';
}

export function friendlySurfaceErrorForLocale(raw, locale) {
  const c = getAssessmentPageCopy(locale);
  const s = String(raw || '').trim();
  if (!s) return '';
  if (
    /Could not find Chrome|Puppeteer|chromium|chrome-headless|\.cache\/puppeteer|npx puppeteer|\/home\/|ERR_|devtools|BrowserLauncher/i.test(
      s,
    )
  ) {
    return c.friendlyPdfChrome;
  }
  if (/Missing GEMINI|Missing OPENAI|Set OPENAI|Configure OPENAI|Invalid REPORT_LLM|requires.*API_KEY|GEMINI_API_KEY|OPENAI_API_KEY/i.test(s)) {
    return c.friendlyLlm;
  }
  if (/Illegal mix of collations|utf8mb4_general_ci.*utf8mb4_unicode_ci|utf8mb4_unicode_ci.*utf8mb4_general_ci|ER_CANT_AGGREGATE_2COLLATIONS|for operation 'nullif'/i.test(s)) {
    return c.friendlyDbCollation;
  }
  /* Long server errors (MySQL, Puppeteer) used to become a useless generic — show a safe prefix instead. */
  const maxKeep = process.env.NODE_ENV === 'production' ? 360 : 900;
  if (s.length > maxKeep) {
    const oneLine = s.slice(0, maxKeep).replace(/\s+/g, ' ').trim();
    return `${oneLine}…`;
  }
  if (/We could not finish the report right now/i.test(s)) {
    return c.friendlyReportServerGuard;
  }
  if (/Load failed|Failed to fetch|NetworkError|network request failed/i.test(s)) {
    return process.env.NODE_ENV === 'production' ? c.friendlyNetworkDeployed : c.friendlyNetwork;
  }
  if (/ECONNRESET|ECONNREFUSED|socket hang up|network.*reset/i.test(s)) {
    return process.env.NODE_ENV === 'production' ? c.friendlyNetworkDeployed : c.friendlyNetwork;
  }
  return s;
}
